/**
 * Compute the year-end annual report shown to the player on January 1st.
 * Returns a structured payload that's persisted in `state.annualReports[year]`
 * and surfaced in /app/frontend/src/components/dashboard/AnnualReportModal.jsx.
 *
 * The metrics are intentionally simple and explainable — this is a narrative
 * piece, not an analytics dashboard. We surface:
 *   - top 3 drivers by productivity × satisfaction
 *   - worst 3 assignments (active drivers with sat < 40 ≥ 60 days on the job)
 *   - burnout alerts resolved in the year (proxy for retention saves)
 *   - Climat Social score /100 (50 % avg satisfaction, 50 % retention rate)
 *   - financial highlights from yearlyReports if available
 */
export const computeAnnualReport = (state, closingYear) => {
  const drivers = state.drivers || [];

  // Top 3 by productivity × satisfaction (proxy = totalKm × sat / 100).
  const ranked = drivers
    .map(d => ({
      id: d.id,
      name: d.name,
      satisfaction: d.satisfaction || 0,
      totalKm: d.totalKm || 0,
      totalTrips: d.totalTrips || 0,
      score: (d.totalKm || 0) * Math.max(0, d.satisfaction || 0) / 100,
    }))
    .sort((a, b) => b.score - a.score);
  const topDrivers = ranked.slice(0, 3).filter(d => d.totalKm > 0);

  // Worst assignments: active drivers, satisfaction < 40, on the road > 60 days.
  const worstAssignments = drivers
    .filter(d => d.status === 'active' && (d.satisfaction || 0) < 40)
    .map(d => ({
      id: d.id,
      name: d.name,
      satisfaction: d.satisfaction || 0,
      assignedVehicleId: d.assignedVehicleId,
    }))
    .slice(0, 3);

  // Climat social: 50 % avg satisfaction + 50 % retention (1 - 12-month turnover rate).
  const avgSat = drivers.length > 0
    ? drivers.reduce((s, d) => s + (d.satisfaction || 0), 0) / drivers.length
    : 70;
  // 12-month turnover proxy: number of hired drivers minus current drivers,
  // divided by (current + hired). Bounded [0, 1]. Falls back to 0 when there's
  // no firing history.
  const archivedCount = Object.keys(state.firedDriversArchive || {}).length;
  const totalKnown = archivedCount + drivers.length;
  const turnoverRate = totalKnown > 0 ? archivedCount / totalKnown : 0;
  const retentionPct = (1 - Math.min(1, turnoverRate)) * 100;
  const climatSocial = Math.round(avgSat * 0.5 + retentionPct * 0.5);

  // Year financial summary (best-effort — uses cumulative monthlyHistory).
  const monthlyHist = (state.monthlyHistory || []).filter(m => m.year === closingYear);
  const yearRevenue = monthlyHist.reduce((s, m) => s + (m.revenue || 0), 0);
  const yearExpenses = monthlyHist.reduce((s, m) => s + (m.expenses || 0), 0);
  const yearProfit = yearRevenue - yearExpenses;

  return {
    year: closingYear,
    generatedAt: { day: state.day, month: state.month, year: state.year, totalDays: state.totalDays },
    topDrivers,
    worstAssignments,
    burnoutAlertsResolved: state.burnoutAlertsResolvedYearly || 0,
    // 25/07/2026 (retour user « 0 démissions évitées, c'est faux ») —
    // total des départs évités = alertes burnout traitées + rétentions
    // actives (renégociations accordées, contre-débauchages, alignements,
    // congés sous pression) tracées dans retentionSavesYearly.
    retentionSaves: (state.burnoutAlertsResolvedYearly || 0) + (state.retentionSavesYearly || []).length,
    retentionSavesDetail: {
      burnout: state.burnoutAlertsResolvedYearly || 0,
      renego: (state.retentionSavesYearly || []).filter(s => s.type === 'renego').length,
      align: (state.retentionSavesYearly || []).filter(s => s.type === 'align').length,
      poach: (state.retentionSavesYearly || []).filter(s => s.type === 'poach').length,
      pressure: (state.retentionSavesYearly || []).filter(s => s.type === 'pressure').length,
    },
    retentionSavesNames: (state.retentionSavesYearly || []).slice(-6).map(s => s.name).filter(Boolean),
    climatSocial,
    avgSatisfaction: Math.round(avgSat),
    turnoverRate: Math.round(turnoverRate * 100),
    yearRevenue,
    yearExpenses,
    yearProfit,
    fleetSize: (state.vehicles || []).length,
    teamSize: drivers.length,
    reputation: Math.round(state.reputation || 0),
    notoriety: Math.round(state.notoriety || 0),
  };
};
